import { ThemeProvider } from 'styled-components';
import { useTranslation } from '@klaviyo-mkt/gatsby-components/hooks/useTranslation';
import { windowIsDefined } from '@klaviyo-mkt/gatsby-components/shared/utils/functions';

import { InstantSearch, Configure } from 'react-instantsearch';

import { useSiteMetadataQuery } from '@klaviyo-mkt/gatsby-theme-core/src/hooks/useSiteMetadataQuery';
import { useHelpCenterCTAData } from '@klaviyo-mkt/gatsby-theme-core/src/hooks/useHelpCenterCTAData';

import { useUniversalNavigationQuery } from '@klaviyo-mkt/gatsby-source-universal-navigation/src/hooks/useUniversalNavigationQuery';

import { Header as UniversalHeader } from '@klaviyo-mkt/gatsby-components/organisms/header';
import { UniversalFooter } from '@klaviyo-mkt/gatsby-components/organisms/universal-footer';
import Header from '@klaviyo-mkt/gatsby-components/molecules/header';
import KlaviyoStatusIndicator from '@klaviyo-mkt/gatsby-components/organisms/klaviyo-status-indicator';
import MoreCards from '@klaviyo-mkt/gatsby-components/organisms/more-cards';
import InstantResults from '@klaviyo-mkt/gatsby-components/shared/search/instant-results';
import SearchBox, { StaticSearchBox } from '@klaviyo-mkt/gatsby-components/molecules/search-box';
import { default as defaultTheme } from '@klaviyo-mkt/gatsby-components/shared/styles/themes/default';
import { searchClient as algoliaClient } from '@klaviyo-mkt/gatsby-components/utils/search-client';

import { convertLocaleForIconName } from '@klaviyo-mkt/gatsby-components/atoms/icon/utils';

import { sites } from '../constants';
import { useMoreCardsData } from './hooks/useMoreCardsData';
import * as s from './styles';

import type { SearchClient } from 'algoliasearch/lite';
import type { TIconName } from '@klaviyo-mkt/gatsby-components/atoms/icon/types';
import type { IUniversalMenuData } from '@klaviyo-mkt/gatsby-source-universal-navigation/src/types';
import type { IHCLayout } from './types';


const indexName = 'help_center_articles';

const searchClient = {
  ...algoliaClient,
  search(requests: any) {
    if (requests.every(({ params }: any) => !params.query)) {
      return Promise.resolve({
        results: requests.map(() => ({
          hits: [],
          nbHits: 0,
          nbPages: 0,
          page: 0,
          processingTimeMS: 0,
          hitsPerPage: 0,
          exhaustiveNbHits: false,
          query: '',
          params: '',
        })),
      });
    }

    return algoliaClient.search(requests);
  },
} satisfies SearchClient;

const ConditionalInstantSearch = () => {
  const { t } = useTranslation();

  if (windowIsDefined()) {
    return (
      <InstantSearch
        indexName={indexName}
        searchClient={searchClient}
        insights
      >
        <Configure hitsPerPage={5} filters={indexName === 'help_center_articles' ? 'locale:en-us' : ''}/>
        <s.SearchWrapper>
          <s.KlaviyoFlag iconName="BugMarkFilled"/>
          <s.SearchHeadline colorVariant="dark" variant="h2">{t('helpCenter_layout_searchHeadline')}</s.SearchHeadline>
          <SearchBox
            searchPage={'/search'}
            instantResults={<InstantResults/>}
            indexName="help_center_articles"
          />
        </s.SearchWrapper>
      </InstantSearch>
    );
  }

  // server rendered
  return (
    <s.SearchWrapper>
      <s.KlaviyoFlag iconName="BugMarkFilled"/>
      <s.SearchHeadline colorVariant="dark" variant="h2">{t('helpCenter_layout_searchHeadline')}</s.SearchHeadline>
      <StaticSearchBox />
    </s.SearchWrapper>
  );
};

const Layout = ({
  children,
  headerChildren,
  path = '',
  showFooter = true,
  showHeader = true,
  showMoreCards = true,
  translations = [],
}: IHCLayout) => {
  const { t, i18n } = useTranslation();

  const ctas = useHelpCenterCTAData();
  const moreCards = useMoreCardsData();
  const siteMetadata = useSiteMetadataQuery();
  /* @ts-ignore: this is valid, I can't narrow the return type */
  const universalNavData: { [ x: string ]: IUniversalMenuData; } = useUniversalNavigationQuery({ multiLocale: true });
  const { copyright, footer, helpCenter, utility }: IUniversalMenuData = universalNavData?.[i18n.language] ?? universalNavData?.['en-US'] ?? {};
  return (
    <ThemeProvider theme={defaultTheme}>
        <s.SiteWrapper>
          {showHeader && (
            <UniversalHeader
              homeLink={`/hc/${t('langCodes_localeLowerCase')}`}
              path={path}
              desktopLogoIconName={`KlaviyoPowerUpLogo${convertLocaleForIconName(i18n.language)}` as TIconName || 'Logo'}
              searchLink={`/hc/${t('langCodes_localeLowerCase')}/search`}
              siteMetadata={siteMetadata}
              universalCtas={ctas}
              universalNavigationItems={helpCenter}
              navigationStyle='power_up'
              utilityChildren={<KlaviyoStatusIndicator />}
              utilityNavigationItems={utility}
              translations={translations}
              sites={sites}
            />
          )}
          <main id="site-content" role="main">
            <Header>
              {headerChildren || (
                <ConditionalInstantSearch />
              )}
            </Header>
            {children}
            {showMoreCards && (
              <MoreCards
                headline={t('helpCenter_layout_moreCardHeadline')}
                moreCards={moreCards}
              />
            )}
          </main>
          {showFooter && (
            <UniversalFooter
              langCode={i18n.language.toLowerCase()}
              copyrightMenuItems={copyright}
              menuItems={footer}
              siteMetadata={siteMetadata}
              sites={sites}
              translations={translations}
            />
          )}
        </s.SiteWrapper>
    </ThemeProvider>
  );
};

export default Layout;
